import { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { AlertTriangle, Clock, CheckCircle2, AlertCircle, FileText, ArrowRight, CreditCard, MessageSquarePlus, Timer, Send, Paperclip, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import PatientSurveyCard from "@/components/PatientSurveyCard";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== MAPPING CENTRAL ====================
// Español neutro de LATAM - evitar voseo/tuteo exclusivo de una región
const getConsultStatusInfo = (status, disposition, escalation, consultId) => {
  
  // Prioridad 1: ESCALATED_BY_DOCTOR o ESCALATED
  // El médico revisó la consulta y determinó que requiere atención adicional
  if (disposition === 'ESCALATED_BY_DOCTOR' || status === 'ESCALATED') {
    const timeframe = escalation?.recommended_timeframe_label || '';
    const reason = escalation?.clinical_reason || '';
    return {
      title: "Tu consulta fue derivada",
      message: `El profesional médico revisó tu caso y determinó que necesitas una evaluación adicional que no puede resolverse por este medio.${timeframe ? ` Te recomendamos gestionar la atención ${timeframe.toLowerCase()}.` : ''}`,
      severity: "warning",
      badge: { label: "Derivada", color: "bg-purple-100 text-purple-700 border-purple-200" },
      icon: ArrowRight,
      showEscalationDetails: true,
      clinicalReason: reason
    };
  }

  // Prioridad 2: BLOCKED_URGENT o UrgentBlocked
  // Los síntomas reportados requieren atención presencial inmediata
  if (disposition === 'BLOCKED_URGENT' || status === 'UrgentBlocked') {
    return {
      title: "Atención presencial requerida",
      message: "Los síntomas que reportaste requieren evaluación médica presencial. Por favor, acude a una guardia o servicio de emergencias lo antes posible.",
      severity: "danger",
      badge: { label: "Atención urgente", color: "bg-red-100 text-red-700 border-red-200" },
      icon: AlertTriangle,
      showEmergencyWarning: true
    };
  }

  // Prioridad 3: Estados operativos
  switch (status) {
    case 'PendingPayment':
      return {
        title: "Pago pendiente",
        message: "Para que tu consulta sea revisada por el equipo médico, necesitas completar el pago.",
        severity: "warning",
        badge: { label: "Pendiente de pago", color: "bg-yellow-100 text-yellow-700 border-yellow-200" },
        icon: CreditCard,
        cta: consultId ? { label: "Completar pago", url: `/payment/${consultId}` } : null
      };

    case 'Paid':
    case 'InReview':
      return {
        title: "Tu consulta está en revisión",
        message: "El equipo médico está revisando tu consulta. Te enviaremos un correo electrónico cuando esté lista la respuesta.",
        severity: "info",
        badge: { label: "En revisión", color: "bg-blue-100 text-blue-700 border-blue-200" },
        icon: Clock
      };

    case 'Responded':
      return {
        title: "Tu consulta fue respondida",
        message: "El profesional médico ya respondió tu consulta. Puedes leer la respuesta a continuación. También la recibiste en tu correo electrónico.",
        severity: "success",
        badge: { label: "Respondida", color: "bg-green-100 text-green-700 border-green-200" },
        icon: CheckCircle2
      };

    case 'RejectedByTriage':
      return {
        title: "Requiere evaluación presencial",
        message: "Según los síntomas que indicaste, tu caso necesita evaluación médica presencial. Este tipo de consulta no puede resolverse de forma remota.",
        severity: "warning",
        badge: { label: "Derivada a presencial", color: "bg-orange-100 text-orange-700 border-orange-200" },
        icon: AlertCircle
      };

    case 'AWAITING_INFO':
      return {
        title: "Tu médico necesita más información",
        message: "El profesional médico revisó tu consulta y necesita información adicional antes de poder darte una respuesta completa.",
        severity: "warning",
        badge: { label: "Info solicitada", color: "bg-amber-100 text-amber-700 border-amber-200" },
        icon: MessageSquarePlus,
        showInfoRequestForm: true
      };

    case 'INFO_RECEIVED':
      return {
        title: "Información recibida",
        message: "Ya enviaste la información solicitada. El equipo médico está revisando tu respuesta y te contactará pronto.",
        severity: "info",
        badge: { label: "En revisión", color: "bg-purple-100 text-purple-700 border-purple-200" },
        icon: Clock
      };

    case 'CLOSED_TIMEOUT':
      return {
        title: "Consulta cerrada",
        message: "Tu consulta fue cerrada porque no respondiste la solicitud de información adicional dentro del plazo establecido (7 días). Si aún necesitas atención médica, puedes iniciar una nueva consulta.",
        severity: "warning",
        badge: { label: "Cerrada por timeout", color: "bg-slate-100 text-slate-700 border-slate-200" },
        icon: XCircle
      };

    default:
      return {
        title: "Estado de tu consulta",
        message: "Tu consulta está siendo procesada. Te notificaremos cuando haya una actualización.",
        severity: "info",
        badge: { label: status || "Procesando", color: "bg-slate-100 text-slate-700 border-slate-200" },
        icon: FileText
      };
  }
};

// Severity styles
const severityStyles = {
  info: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-800", title: "text-blue-900" },
  warning: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", title: "text-amber-900" },
  danger: { bg: "bg-red-50", border: "border-red-200", text: "text-red-800", title: "text-red-900" },
  success: { bg: "bg-green-50", border: "border-green-200", text: "text-green-800", title: "text-green-900" }
};

// Patient Info Response Form Component
const PatientInfoResponseForm = ({ consult, accessToken, onSuccess }) => {
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Calculate time remaining
  const getTimeRemaining = () => {
    if (!consult.info_request_deadline) return null;
    
    const deadline = new Date(consult.info_request_deadline);
    const now = new Date();
    const diff = deadline - now;
    
    if (diff <= 0) return "Plazo vencido";
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) {
      return `${days} día${days > 1 ? 's' : ''}, ${hours} hora${hours > 1 ? 's' : ''}`;
    }
    return `${hours} hora${hours > 1 ? 's' : ''}`;
  };

  const handleSubmit = async () => {
    if (additionalInfo.length < 5) {
      toast.error("La respuesta debe tener al menos 5 caracteres");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/consults/${consult.id}/submit-additional-info?access_token=${accessToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ additional_info: additionalInfo })
      });

      if (response.ok) {
        toast.success("Información enviada correctamente");
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al enviar la información");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Error al conectar con el servidor");
    } finally {
      setSubmitting(false);
    }
  };

  const timeRemaining = getTimeRemaining();
  const isExpired = timeRemaining === "Plazo vencido";
  const deadline = consult.info_request_deadline 
    ? new Date(consult.info_request_deadline).toLocaleDateString('es-AR', { 
        day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' 
      })
    : null;

  return (
    <div className="mt-6 bg-white rounded-xl border border-amber-200 p-6" data-testid="patient-info-form">
      {/* Doctor's request */}
      <div className="mb-6">
        <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
          <MessageSquarePlus className="h-5 w-5 text-amber-600" />
          Mensaje del médico
        </h3>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-slate-700 italic">"{consult.info_request_text}"</p>
        </div>
      </div>

      {/* No-charge reassurance */}
      <div className="mb-6 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-2.5" data-testid="no-charge-reassurance">
        <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-emerald-900">
          <strong>Sin cargo aún.</strong> Recién se realiza el cobro cuando recibís la respuesta médica completa.
        </p>
      </div>

      {/* Time remaining warning */}
      <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
        isExpired ? 'bg-red-50 border border-red-200' : 'bg-blue-50 border border-blue-200'
      }`}>
        <Timer className={`h-5 w-5 ${isExpired ? 'text-red-600' : 'text-blue-600'}`} />
        <div>
          <p className={`font-medium ${isExpired ? 'text-red-700' : 'text-blue-700'}`}>
            {isExpired ? 'El plazo para responder ha vencido' : `Tiempo restante: ${timeRemaining}`}
          </p>
          {deadline && !isExpired && (
            <p className="text-sm text-slate-600">Fecha límite: {deadline}</p>
          )}
        </div>
      </div>

      {!isExpired && (
        <>
          {/* Response textarea */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Tu respuesta *
            </label>
            <Textarea
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
              placeholder="Escribe aquí la información que solicita tu médico..."
              rows={5}
              className="resize-none"
              data-testid="additional-info-textarea"
            />
            <p className="text-xs text-slate-500 mt-1">
              {additionalInfo.length} caracteres (mínimo 5)
            </p>
          </div>

          {/* Submit button */}
          <Button
            onClick={handleSubmit}
            disabled={submitting || additionalInfo.length < 5}
            className="w-full h-12 bg-amber-500 hover:bg-amber-600"
            data-testid="submit-additional-info-btn"
          >
            {submitting ? "Enviando..." : "Enviar información"}
            <Send className="ml-2 h-5 w-5" />
          </Button>
        </>
      )}
    </div>
  );
};

const ConsultStatusPage = () => {
  const { consultId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('t');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchStatus = async () => {
      if (!token) {
        setError("El enlace no es válido. Verifica que hayas copiado la URL completa desde el correo electrónico.");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/consults/${consultId}/public?t=${token}`);
        
        if (response.ok) {
          const result = await response.json();
          setData(result);
        } else if (response.status === 403) {
          const errorData = await response.json();
          if (errorData.detail === "Enlace expirado.") {
            setError("Este enlace ya expiró. Por razones de seguridad, los enlaces de consulta tienen una validez de 30 días. Si necesitas acceder a tu consulta, contacta al equipo médico.");
          } else {
            setError("El enlace no es válido. Verifica que hayas copiado la URL completa desde el correo electrónico.");
          }
        } else if (response.status === 404) {
          setError("No encontramos esta consulta. Es posible que haya sido eliminada o que el enlace sea incorrecto.");
        } else {
          setError("Ocurrió un error al cargar tu consulta. Por favor, intenta de nuevo más tarde.");
        }
      } catch (err) {
        console.error('Error:', err);
        setError("No pudimos conectar con el servidor. Verifica tu conexión a internet e intenta de nuevo.");
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [consultId, token]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Cargando tu consulta...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50">
        <nav className="border-b border-slate-200 bg-white">
          <div className="max-w-2xl mx-auto px-4 h-16 flex items-center">
            <img src="/logo-fleni.png" alt="Fleni" className="h-8 sm:h-10 object-contain" />
          </div>
        </nav>
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-white rounded-xl border border-red-200 p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-slate-900 mb-2">No pudimos cargar tu consulta</h1>
            <p className="text-slate-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Get status info using central mapping
  const statusInfo = getConsultStatusInfo(data.status, data.disposition, data.escalation, consultId);
  const StatusIcon = statusInfo.icon;
  const styles = severityStyles[statusInfo.severity];
  
  // Check if emergency warning should show (only for urgency_level critica)
  const showEmergencyWarning = statusInfo.showEmergencyWarning || 
    (data.escalation && data.escalation.urgency_level_label === 'Crítica');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="border-b border-slate-200 bg-white">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center">
          <img src="/logo-fleni.png" alt="Fleni" className="h-8 sm:h-10 object-contain" />
        </div>
      </nav>

      {/* Urgent Banner - only for danger severity */}
      {statusInfo.severity === 'danger' && (
        <div className="bg-red-600 text-white py-3 px-4">
          <div className="max-w-2xl mx-auto flex items-center justify-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            <span className="font-medium">ATENCIÓN URGENTE REQUERIDA</span>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          
          {/* Status Header */}
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-xl font-semibold text-slate-900">Estado de tu consulta</h1>
              <Badge className={`${statusInfo.badge.color} border px-3 py-1`}>
                <StatusIcon className="h-4 w-4 mr-1" />
                {statusInfo.badge.label}
              </Badge>
            </div>
            <p className="text-sm text-slate-500">
              ID: {data.consult_id.slice(0, 8)}... • Creada: {new Date(data.created_at).toLocaleDateString('es-AR')}
            </p>
          </div>

          {/* Main Message */}
          <div className={`p-6 ${styles.bg} border-b ${styles.border}`}>
            <h2 className={`font-semibold ${styles.title} mb-2`}>{statusInfo.title}</h2>
            <p className={styles.text}>{statusInfo.message}</p>
            
            {/* CTA Button */}
            {statusInfo.cta && (
              <Button 
                onClick={() => navigate(statusInfo.cta.url)}
                className="mt-4 bg-amber-600 hover:bg-amber-700"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                {statusInfo.cta.label}
              </Button>
            )}
          </div>

          {/* Doctor Response - visible once responded */}
          {data.response_text && (
            <div className="p-6 border-b border-slate-100" data-testid="patient-response-block">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-1">
                Respuesta del profesional
              </h3>
              {(data.doctor_name || data.responded_at) && (
                <p className="text-xs text-slate-400 mb-3">
                  {data.doctor_name ? `Por ${data.doctor_name}` : ""}
                  {data.doctor_name && data.responded_at ? " • " : ""}
                  {data.responded_at ? new Date(data.responded_at).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' }) : ""}
                </p>
              )}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-slate-800 leading-relaxed whitespace-pre-wrap text-sm">
                {data.response_text}
              </div>
            </div>
          )}

          {/* Escalation Details */}
          {statusInfo.showEscalationDetails && data.escalation && (
            <div className="p-6 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">Detalles de la derivación</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-600">Tipo de atención recomendada:</span>
                  <span className="font-medium text-slate-900">{data.escalation.escalation_type_label}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Nivel de urgencia:</span>
                  <span className={`font-medium ${
                    data.escalation.urgency_level_label === 'Crítica' ? 'text-red-600' :
                    data.escalation.urgency_level_label === 'Alta' ? 'text-orange-600' :
                    data.escalation.urgency_level_label === 'Media' ? 'text-yellow-600' :
                    'text-blue-600'
                  }`}>{data.escalation.urgency_level_label}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Plazo recomendado:</span>
                  <span className="font-medium text-slate-900">{data.escalation.recommended_timeframe_label}</span>
                </div>
                {statusInfo.clinicalReason && (
                  <div className="pt-3 border-t border-slate-100">
                    <span className="text-slate-600 text-sm block mb-2">Motivo clínico informado por el profesional:</span>
                    <p className="text-slate-800 bg-slate-50 p-3 rounded text-sm">{statusInfo.clinicalReason}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Emergency Warning - ONLY for urgency_level critica */}
          {showEmergencyWarning && (
            <div className="p-6 bg-red-50 border-b border-red-100">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-900 mb-1">Advertencia importante</p>
                  <p className="text-sm text-red-800">
                    Si presentas dificultad respiratoria, hinchazón facial, pérdida de conocimiento, dolor intenso o empeoramiento súbito, acude a un servicio de emergencias de inmediato.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Patient Info Response Form - ONLY for AWAITING_INFO status */}
          {statusInfo.showInfoRequestForm && data.info_request_text && (
            <div className="p-6 border-b border-slate-100">
              <PatientInfoResponseForm 
                consult={{
                  id: data.consult_id,
                  info_request_text: data.info_request_text,
                  info_request_deadline: data.info_request_deadline
                }}
                accessToken={token}
                onSuccess={() => window.location.reload()}
              />
            </div>
          )}

          {/* Post-response Survey - excluded for recetas (backend flag) */}
          {data.survey_eligible && (
            <div className="p-6 border-b border-slate-100">
              <PatientSurveyCard
                consultId={consultId}
                accessToken={token}
                escalated={data.status === 'ESCALATED'}
                alreadySubmitted={data.survey_submitted}
              />
            </div>
          )}

          {/* Footer */}
          <div className="p-6 text-center">
            <p className="text-xs text-slate-500">
              Si tienes dudas, puedes contactarnos respondiendo al correo electrónico que recibiste.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultStatusPage;
